import { useSelector } from 'react-redux';
import { RootState } from '../redux/Store';
import http from './http';
import { isComingFromMainWindow } from '../utils/helpers/useFindPermissions';
import { Platform } from '../constants/platform';
/**
 * Custom hook to retrieve the audit status from the Redux store.
 */
export const useAuditStatus = () => {
  return useSelector((state: RootState) => state.contextId.queryObj);
};

const value = isComingFromMainWindow();
const isInspect = Platform.isInspect();

const createApiRequest = (baseUrl: string) => (requestPath: string) => ({
  auth: async <T>(requestBody: T) =>
    http({ baseUrl, isAuth: true }).post(requestPath, requestBody),
  create: async <T>(requestBody: T) =>
    http({ baseUrl }).post(requestPath, requestBody),
  get: async () =>
    http({ baseUrl }).get(`${requestPath}`, { withCredentials: true }),
  getAll: async <T>(queryParameters: T) =>
    http({ baseUrl }).get(
      `${requestPath}?${new URLSearchParams(queryParameters as any).toString()}`
    ),
  getById: async (id: string | number, extraId = '') =>
    http({ baseUrl }).get(
      `${requestPath}/${id}${extraId ? `/${extraId}` : ''}`
    ),
  updateById: async <T>(id: string, requestBody: T) =>
    http({ baseUrl }).put(`${requestPath}/${id}`, requestBody),
  deleteById: async (id: string | number) =>
    http({ baseUrl }).delete(`${requestPath}/${id}`),
});

const ApiDomains = {
  safeview: `https://safeviewapi.${process.env.REACT_APP_API_DOMAIN}`,
  corescript: `https://corescriptapi.${process.env.REACT_APP_API_DOMAIN}`,
  media: `https://mediav2.${process.env.REACT_APP_API_DOMAIN}/media/v2`,
  rolemaster: `https://rolemasterv1.${process.env.REACT_APP_API_DOMAIN}/rolemaster/v1`,
  spiglassapi: `https://spiglassapi.${process.env.REACT_APP_API_DOMAIN}`,
};

const CoreScriptAPI = createApiRequest(ApiDomains.corescript);
const SafeViewAPI = createApiRequest(ApiDomains.safeview);
const MediaAPI = createApiRequest(ApiDomains.media);
const RolemasterAPI = createApiRequest(ApiDomains.rolemaster);

/**
 * Factory functions for creating request templates for different routePaths.
 */
const SCRIPT_REQUEST = (requestPath: string) => ({
  auth: async <T>(requestBody: T) =>
    await http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
      isAuth: true,
    }).post(requestPath, requestBody),
  create: async <T>(requestBody: T) =>
    await http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).post(requestPath, requestBody),
  getAll: async <T>(queryParameters: T) =>
    http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).get(`${requestPath}?${queryParameters}`),
  get: async () =>
    http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).get(`${requestPath}`, {
      withCredentials: true,
    }),
  getById: async (id: string | number, extraId = '') =>
    http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).get(`${requestPath}/${id}${extraId ? `/${extraId}` : ''}`, {
      withCredentials: true,
    }),
  getByCoreScriptId: async (id: string | number) =>
    http({ baseUrl: ApiDomains?.corescript }).get(`${requestPath}/${id}`, {
      withCredentials: true,
    }),
  getContent: async (parameters: any) =>
    http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).get(`${requestPath}/${parameters}`, { withCredentials: true }),
  getByIdQuery: async (id: string) =>
    http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).get(`${requestPath}?id=${id}`, {
      withCredentials: true,
    }),
  updateByIdroutePath: async <T>(id: string, requestBody: T) =>
    http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).put(`${requestPath}/${id}`, requestBody),
  updateWithOutId: async <T>(requestBody: T) =>
    http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).put(requestPath, requestBody),
  cloneMediaFile: async <T>(id: string, requestBody: T) =>
    await http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).put(`${requestPath}/${id}`, requestBody),
  fetchMediaMetaByGuid: async <T>(id: string, requestBody: T) =>
    await http({
      baseUrl: isInspect ? ApiDomains?.safeview : ApiDomains?.corescript,
    }).post(`${requestPath}/${id}`, requestBody),
});

const CallBack = (requestPath: string) => ({
  getAll: async <T>(queryParameters: T) =>
    http({ baseUrl: ApiDomains?.corescript }).get(
      `${requestPath}?${queryParameters}`
    ),
});
const MEDIA_REQUEST = (requestPath: string) => ({
  getById: async (id: string | number) =>
    http({ baseUrl: ApiDomains?.media }).get(`${requestPath}/${id}`, {
      withCredentials: true,
    }),
  uploadMediaFile: async <T>(requestBody: T) =>
    await http({ baseUrl: ApiDomains?.media }).post(requestPath, requestBody),
  putMediaFile: async <T>(requestBody: T) =>
    await http({ baseUrl: ApiDomains?.media }).put(requestPath, requestBody),
  postRequest: async <T>(requestBody: T) =>
    await http({ baseUrl: ApiDomains?.media }).post(requestPath, requestBody),
});

/**
 * Example usage of the request templates in different sections.
 */
export const useRequestSections = () => {
  const queryObj = useAuditStatus(); // Use the custom
  const versions = {
    v1: 'v1',
    v3: 'v3',
    v4: 'v4',
  };

  const Script = {
    getScriptHash: CoreScriptAPI(
      `scripthash/${queryObj?.context}/${queryObj?.key}`
    ).getById,

    getScript: CoreScriptAPI(`GetScript`).getById,
    getScriptLanguagePack: CoreScriptAPI(`GetScriptLanguagePackPresignURL/EN`)
      .getById,
    getCallBack: (method: string, parameters: any) =>
      CallBack(method).getAll(parameters),
  };

  const Decision = {
    getDecisionReasons: CoreScriptAPI(
      `decisionreasons/${queryObj?.context}/${queryObj?.key}`
    ).getById,
  };

  const Seeded = {
    getSeeded: CoreScriptAPI(`seeded/${queryObj?.context}/${queryObj?.key}`)
      .getById,
    getAuditResults: SCRIPT_REQUEST(
      `seeded/AuditResultsById/ParentSubmission.ID`
    ).getByCoreScriptId,
  };

  const Current_Answer = {
    getCurrentAnswers: CoreScriptAPI(
      `currentanswers/${queryObj?.context}/${queryObj?.key}`
    ).getById,
  };

  const Audit_Failure = {
    getAuditFailures: CoreScriptAPI(
      `auditfailures/${queryObj?.context}/${queryObj?.key}`
    ).getById,
  };

  const Prior_Decisions = {
    getPriorDecisions: CoreScriptAPI(
      `priordecisions/${queryObj?.context}/${queryObj?.key}`
    ).getById,
  };

  const Luggage = {
    getLuggage: CoreScriptAPI(`luggage/${queryObj?.context}/${queryObj?.key}`)
      .getById,
    getLuggageSV: SafeViewAPI(`luggage/workorder/v1/lite`).getById,
  };

  const ImagesApi = {
    getImages: CoreScriptAPI(`images/${queryObj?.context}/${queryObj?.key}`)
      .getById,
    postOrderImage: (parameters: any) =>
      MEDIA_REQUEST(`postorderimage`).uploadMediaFile(parameters),
    deleteOrderImage: (payload: any) =>
      MEDIA_REQUEST(`deleteopenorderimage`).postRequest(payload),
    putURLOrderImage: MEDIA_REQUEST(``).putMediaFile,
    checkImageStatus: (payload: any) =>
      MEDIA_REQUEST('altimagestatus').postRequest(payload),
    cloneImage: SCRIPT_REQUEST(
      `clonemedia/${queryObj?.context}/${queryObj?.key}`
    ).cloneMediaFile,
    fetchImageMetaByGuid: SCRIPT_REQUEST(
      'imagesbyguid/AuditResults/WorkOrder.ContextId'
    ).fetchMediaMetaByGuid,
  };

  const Save = {
    saveData: SCRIPT_REQUEST(`save/${queryObj?.context}/${queryObj?.key}`)
      .updateByIdroutePath,
  };

  const EvaluateBilling = {
    evaluateBilling: SCRIPT_REQUEST(
      `evaluatebilling/${queryObj?.context}/${queryObj?.key}`
    ).updateByIdroutePath,
  };

  const History = {
    getHistory: SCRIPT_REQUEST(`history/${queryObj?.context}/${queryObj?.key}`)
      .fetchMediaMetaByGuid,
    getHistoryTrail: SCRIPT_REQUEST(
      `audittrail/${queryObj?.context}/${queryObj?.key}`
    ).fetchMediaMetaByGuid,
  };

  const Permission = {
    getPermission: RolemasterAPI(`getfunctionsbytoken`).get,
  };

  const Search = {
    getOrderSearchResults: SafeViewAPI(`searchv4/${versions?.v1}/ordernumber`)
      .getById,
  };

  const Submission = {
    getWorkorderSubmissions: SafeViewAPI(`submission/${versions?.v1}/workorder`)
      .getById,
  };

  const LanguagePackTermsetModule = {
    getLanguagepackTermsetsByModule: SafeViewAPI(
      `languagepack/${versions?.v1}/workorder/termset/EN/SV_WorkOrderLeftSidebar`
    ).get,
  };

  return {
    Script,
    Decision,
    Seeded,
    Current_Answer,
    Audit_Failure,
    Prior_Decisions,
    Luggage,
    ImagesApi,
    Save,
    History,
    Permission,
    Search,
    Submission,
    LanguagePackTermsetModule,
    EvaluateBilling,
  };
};

export const DynamicCallBack = (requestPath: string, api: string) => ({
  get: async () => {
    // Dynamically change the API string
    const adjustedApi = api === 'spiglassapi' ? 'corescript' : api;

    return http({
      baseUrl: ApiDomains[adjustedApi as keyof typeof ApiDomains].toString(),
    }).get(`${requestPath}`, {
      withCredentials: true,
    });
  },
});
